import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import React, { ReactNode, Ref, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { withSession, isGroupAdmin } from '@/state/Session';
import {
  ClickAwayListener,
  Divider,
  Grow,
  ListItemIcon,
  ListItemText,
  MenuList,
  Paper,
  Popper,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import MenuItem from '@material-ui/core/MenuItem';
import HomeIcon from '@material-ui/icons/Home';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import { useDefaultOrganization } from '@/hooks/useDefaultOrganization';

interface HospitalNameProps {
  compressed: boolean;
  children: ReactNode;
  memberships: any;
  selectMembership: any;
  currentOrganizationId?: number | string;
}

const HospitalName = ({
  compressed,
  children,
  groupAdmin,
  memberships,
  selectMembership,
  currentOrganizationId,
}: HospitalNameProps) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>();

  const {
    defaultOrganizationId,
    setDefaultOrganization,
    clearDefaultOrganization,
    isDefaultOrganization,
    sortMembershipsByDefault,
  } = useDefaultOrganization();

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleSetDefault = async (organizationId: number) => {
    await setDefaultOrganization(organizationId);
    setOpen(false);
  };

  const handleClearDefault = async () => {
    await clearDefaultOrganization();
    setOpen(false);
  };

  const hasMultipleOrganizations = !groupAdmin && Array.isArray(memberships) && memberships.length > 1;
  const sortedMemberships = hasMultipleOrganizations ? sortMembershipsByDefault(memberships) : memberships;
  const currentIsDefault = currentOrganizationId && isDefaultOrganization(currentOrganizationId);

  return (
    <>
      {hasMultipleOrganizations ? (
        <div
          ref={anchorRef as Ref<HTMLDivElement>}
          onClick={handleToggle}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        >
          <Typography
            color="textSecondary"
            className={clsx(classes.hospitalName, {
              [classes.hideName]: compressed,
            })}
          >
            {children}
            {currentIsDefault && (
              <HomeIcon
                fontSize="small"
                style={{ marginLeft: 4, verticalAlign: 'middle', opacity: 0.7 }}
                titleAccess="Default Organization"
              />
            )}
          </Typography>
          <ArrowDropDownIcon />
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            style={{
              zIndex: 2000,
            }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id="split-button-menu">
                      {sortedMemberships.map(membership => {
                        const orgIsDefault = isDefaultOrganization(membership.organization.id);
                        return (
                          <MenuItem
                            key={membership.id}
                            onClick={() => selectMembership(membership.id)}
                            style={{ display: 'flex', justifyContent: 'space-between', gap: '1em' }}
                          >
                            <ListItemText primary={membership.organization.name} />
                            {orgIsDefault && (
                              <ListItemIcon style={{ minWidth: 'auto' }}>
                                <HomeIcon fontSize="small" color="primary" titleAccess="Default" />
                              </ListItemIcon>
                            )}
                          </MenuItem>
                        );
                      })}
                      <Divider />
                      {currentOrganizationId && !currentIsDefault && (
                        <MenuItem
                          onClick={() => handleSetDefault(Number(currentOrganizationId))}
                          style={{ fontSize: '0.9em' }}
                        >
                          <ListItemIcon style={{ minWidth: 32 }}>
                            <HomeOutlinedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Set current as default" />
                        </MenuItem>
                      )}
                      {defaultOrganizationId && (
                        <MenuItem onClick={handleClearDefault} style={{ fontSize: '0.9em' }}>
                          <ListItemIcon style={{ minWidth: 32 }}>
                            <HomeOutlinedIcon fontSize="small" color="disabled" />
                          </ListItemIcon>
                          <ListItemText primary="Clear default" />
                        </MenuItem>
                      )}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
      ) : (
        <Typography
          color="textSecondary"
          className={clsx(classes.hospitalName, {
            [classes.hideName]: compressed,
          })}
        >
          {children}
        </Typography>
      )}
    </>
  );
};

export default withSession(context => ({
  groupAdmin: isGroupAdmin(context.session?.user),
  memberships: context.session?.user?.memberships,
  selectMembership: context.selectMembership,
  currentOrganizationId: context.session?.membership?.organization?.id,
}))(HospitalName);

const useStyles = makeStyles(theme => ({
  hospitalName: {
    whiteSpace: 'normal',
  } as any,
  hideName: {
    opacity: 0,
    transition: theme.transitions.create('none', {
      delay: 0,
    }),
  } as any,
}));
